/**
 * Displays an address form when travel rule is triggered and the user must enter
 * in a new address. This happens when a user's only address(es) on file is (are)
 * PO Box address(es). In order for a travel rule-triggered transaction to proceed,
 * they must enter a new, non-PO box address.
 */

import React from 'react'
import PropTypes from 'prop-types'
import { AddressInput, BodyText, AddressInputUtils } from '@paypalcorp/pp-react'
import * as AddressActions from '../actions/address'
import { hasPOBox, isCityPOBox } from '../utils/travelRuleUtils'
import content from 'pp-react-l10n'
let i18n = content('transfer/send/identityInfo')

TravelRuleAddressFields.propTypes = {
  dispatch: PropTypes.func.isRequired,
  country: PropTypes.string.isRequired,
  addressEntryFormat: PropTypes.object.isRequired,
  newTravelRuleAddress: PropTypes.object.isRequired,
  attemptedSubmit: PropTypes.bool.isRequired,
}

export default function TravelRuleAddressFields(props) {
  const { addressParts } = AddressInputUtils.useAddress()
  const {
    addressEntryFormat,
    addressEntryFormat: { strings: labels },
    attemptedSubmit,
    newTravelRuleAddress,
  } = props

  const getErrorMessage = field => {
    const shouldShowInvalidError =
      attemptedSubmit && !newTravelRuleAddress[field.$]
    switch (field.$) {
      case 'line1':
        if (hasPOBox(newTravelRuleAddress[field.$])) {
          return i18n('error.poBox')
        } else if (shouldShowInvalidError) {
          return i18n('error.invalidStreet')
        }
      case 'line2':
        if (hasPOBox(newTravelRuleAddress[field.$])) {
          return i18n('error.poBox')
        }
        return
      case 'city':
        if (isCityPOBox(newTravelRuleAddress[field.$])) {
          return i18n('error.poBox')
        } else if (shouldShowInvalidError) {
          return i18n('error.invalidCity')
        }
      case 'state':
        if (shouldShowInvalidError) {
          return i18n('error.invalidState')
        }
      case 'postcode':
        if (
          newTravelRuleAddress[field.$] &&
          field.$ === 'postcode' &&
          !new RegExp(`^${field.validationPattern}$`).test(
            newTravelRuleAddress[field.$]
          )
        ) {
          return labels.validationError
        } else if (shouldShowInvalidError) {
          return i18n('error.invalidZipCode')
        }
      default:
        if (shouldShowInvalidError) {
          return i18n('error.invalid')
        }
    }
  }

  const getStateList = stateList => {
    return stateList.map(state => {
      const { $id, $elt } = state
      return {
        value: $id,
        label: $elt,
      }
    })
  }

  const handleChange = evt => {
    props.dispatch(
      AddressActions.changeTravelRuleAddressEntryField(
        evt.target.name,
        evt.target.value
      )
    )
  }

  const getAddressEntryFields = field => {
    let fieldsProps = {}
    Object.entries(labels).map(([key, value]) => {
      if (key === `${field.$}Label`) {
        fieldsProps[field.$] = {
          props: {
            label: value,
            name: field.$,
            errorText: getErrorMessage(field),
            value: props.newTravelRuleAddress[field.$],
            id: `travelRule_${field.$}`,
            theme: 'v2',
            onChange: handleChange,
            ...(field.$ !== 'state' &&
              field.$ !== 'postcode' &&
              field.validationPattern && {
                pattern: field.validationPattern,
              }),
            ...(field.$ === 'state' && {
              options: getStateList(labels.stateList),
            }),
          },
        }
      }
    })
    return fieldsProps
  }

  addressEntryFormat.layout
    .filter(field => field._ !== undefined)
    .map(field => Object.assign(addressParts, getAddressEntryFields(field)))

  return (
    <div>
      <BodyText style={{ margin: '1rem 0 0.2rem' }}>
        {i18n('identityInfo.noPOAddressTitle')}
      </BodyText>
      <AddressInput
        region={props.country}
        parts={addressParts}
        partsOrder={['line1', 'line2', 'city', ['state', 'postcode']]}
      />
    </div>
  )
}
